// AI Chat service — Gemini (Google AI) integration + Firestore-backed config
// The config (API key, model, system prompt, enabled flag) is stored under
// content/ai_chat so the public chat widget can read it (admin write).

import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase/config";

// Default fallback so chat works out-of-the-box if no Firestore config exists yet.
// Admin can override via /admin/buckets (Najafli Said section).
const DEFAULT_API_KEY = "AIzaSyCrYYLbIvVGldvsEw5VNpxi0nS_Ul6GvrA";
const DEFAULT_MODEL = "gemini-2.5-flash";
const DEFAULT_SYSTEM_PROMPT =
  "Sən Vinyl Baku mağazasının köməkçi süni intellektisən. Vinyl Baku — Azərbaycanda vinyl plaklar, gramophone, audio aksesuarları və hədiyyə kartları satan onlayn mağazadır. Sən HƏMİŞƏ Azərbaycan dilində danışırsan, mehriban, peşəkar və qısa cavablar verirsən. Müştərilərə vinyl plaklar, sənətçilər, sifariş, çatdırılma, hədiyyə kartları və mağaza ilə bağlı suallarda kömək et. Əgər sual mağaza ilə əlaqəli deyilsə də Azərbaycan dilində nəzakətlə cavab ver. Cavablarda emoji çox işlətmə, sadə və aydın yaz.";

const CFG_DOC = "ai_chat"; // content/ai_chat

// All UI strings shown by the AI chat widget are admin-editable. These
// defaults are returned whenever Firestore is empty / a particular field
// is missing so the widget keeps working out of the box.
export const AI_CHAT_DEFAULTS = Object.freeze({
  api_key: DEFAULT_API_KEY,
  model: DEFAULT_MODEL,
  system_prompt: DEFAULT_SYSTEM_PROMPT,
  enabled: true,
  // ── UI strings ────────────────────────────────────────────────────
  welcome: "Salam! Mən Vinyl Baku süni intellekt köməkçisiyəm. Sizə necə kömək edə bilərəm?",
  header_title: "Vinyl Baku AI",
  header_subtitle: "Köməkçi",
  tooltip_text: "Sizə necə kömək göstərə bilərəm?",
  input_placeholder: "Sualınızı yazın...",
  typing_text: "Yazır…",
  quick_prompts_label: "Sual verin",
  clear_button_text: "Təmizlə",
  fallback_error_text: "Üzr istəyirəm, hal-hazırda cavab verə bilmirəm. Bir az sonra yenidən cəhd edin.",
  // ── Suggested prompt chips on welcome screen ─────────────────────
  suggested_prompts: [
    "Klassik vinyl tövsiyə et",
    "Hədiyyəlik nə alım?",
    "Çatdırılma necə işləyir?",
  ],
  // ── Branding ─────────────────────────────────────────────────────
  // If `logo_url` is set, the widget renders it as a circular avatar in
  // the launcher button, the panel header and every model-message bubble.
  // If empty, the legacy serif "V" mark is used.
  logo_url: "",
  launcher_label: "V",
});

export async function getAIChatConfig() {
  try {
    const snap = await getDoc(doc(db, "content", CFG_DOC));
    if (snap.exists()) {
      const d = snap.data() || {};
      const out = { ...AI_CHAT_DEFAULTS };
      // Merge each field — keep defaults when the Firestore field is missing.
      for (const k of Object.keys(AI_CHAT_DEFAULTS)) {
        if (d[k] !== undefined && d[k] !== null && d[k] !== "") {
          out[k] = d[k];
        }
      }
      // `enabled` is a boolean and can legitimately be `false`
      if (d.enabled === false) out.enabled = false;
      // `suggested_prompts` must be an array — accept stored shape
      if (Array.isArray(d.suggested_prompts)) {
        out.suggested_prompts = d.suggested_prompts.filter((s) => typeof s === "string" && s.trim());
      }
      // Normalize legacy brand spelling "Vinyl Bakı" → "Vinyl Baku" across all
      // text fields (some older Firestore docs still carry the typo).
      for (const k of Object.keys(out)) {
        if (typeof out[k] === "string") {
          out[k] = out[k].replace(/Vinyl Bakı/g, "Vinyl Baku");
        } else if (Array.isArray(out[k])) {
          out[k] = out[k].map((s) =>
            typeof s === "string" ? s.replace(/Vinyl Bakı/g, "Vinyl Baku") : s
          );
        }
      }
      return out;
    }
  } catch (e) {
    console.warn("[aiChat] config read failed, using defaults", e);
  }
  return { ...AI_CHAT_DEFAULTS };
}

export async function saveAIChatConfig(data) {
  await setDoc(
    doc(db, "content", CFG_DOC),
    { ...data, updated_at: serverTimestamp() },
    { merge: true }
  );
}

/**
 * Send a chat turn to Gemini.
 * @param {Object} opts
 * @param {string} opts.apiKey
 * @param {string} opts.model
 * @param {string} opts.systemInstruction
 * @param {Array<{role:'user'|'model', text:string}>} opts.history
 * @param {string} opts.userText
 * @returns {Promise<string>} model reply text
 */
export async function sendGeminiMessage({
  apiKey,
  model = DEFAULT_MODEL,
  systemInstruction = DEFAULT_SYSTEM_PROMPT,
  history = [],
  userText,
}) {
  if (!apiKey) throw new Error("Gemini API key not configured.");
  if (!userText || !userText.trim()) throw new Error("Empty message.");

  const contents = [
    ...history.map((m) => ({
      role: m.role === "model" ? "model" : "user",
      parts: [{ text: m.text }],
    })),
    { role: "user", parts: [{ text: userText }] },
  ];

  const body = {
    contents,
    systemInstruction: { parts: [{ text: systemInstruction }] },
    generationConfig: {
      temperature: 0.7,
      topP: 0.95,
      maxOutputTokens: 1024,
    },
  };

  const url = `https://generativelanguage.googleapis.com/v1beta/models/${encodeURIComponent(
    model
  )}:generateContent?key=${encodeURIComponent(apiKey)}`;

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    let errMsg = `Gemini API error ${res.status}`;
    try {
      const j = await res.json();
      errMsg = j?.error?.message || errMsg;
    } catch (_) { /* ignore */ }
    throw new Error(errMsg);
  }

  const data = await res.json();
  const parts = data?.candidates?.[0]?.content?.parts || [];
  const text = parts.map((p) => p.text || "").join("").trim();
  if (!text) {
    // Could be safety block
    const finish = data?.candidates?.[0]?.finishReason;
    if (finish === "SAFETY") {
      return "Üzr istəyirəm, bu suala cavab verə bilmirəm.";
    }
    return "Üzr istəyirəm, cavab ala bilmədim. Yenidən cəhd edin.";
  }
  return text;
}
